// Inter-School Events (114) -- an invited school's admins/coaches respond to
// pending fixture invitations (accept / decline).

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { FixtureInvitePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CalendarClock, Check, X, Loader2 } from "lucide-react";

function fmt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function FixtureInvitesPage() {
  const [invites, setInvites] = useState<FixtureInvitePublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    api.getFixtureInvites()
      .then((res) => setInvites(res.invites))
      .catch(() => setInvites([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => reload(), [reload]);

  async function respond(inv: FixtureInvitePublic, status: "accepted" | "declined") {
    const key = `${inv.event_id}:${inv.invited_school_id}`;
    setBusyKey(key);
    try {
      await api.respondFixtureInvite(inv.event_id, inv.invited_school_id, status);
      setInvites((prev) => prev.filter((i) => `${i.event_id}:${i.invited_school_id}` !== key));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="flex flex-col gap-4" data-page="fixture-invites">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <CalendarClock className="h-6 w-6 text-primary" aria-hidden /> Fixture invitations
        </h1>
        <p className="text-sm text-muted-foreground">Fixtures other schools have invited your teams to.</p>
      </div>

      {loading ? (
        <div className="h-24 animate-pulse rounded-lg bg-muted" />
      ) : invites.length === 0 ? (
        <div className="rounded-lg border border-border bg-muted/30 px-6 py-10 text-center text-sm text-muted-foreground">
          No pending fixture invitations.
        </div>
      ) : (
        <ul className="flex flex-col gap-3" data-region="invites">
          {invites.map((inv) => {
            const key = `${inv.event_id}:${inv.invited_school_id}`;
            const busy = busyKey === key;
            return (
              <li key={key} className="flex flex-col gap-3 rounded-lg border border-border bg-background p-4 sm:flex-row sm:items-center sm:justify-between" data-invite={key}>
                <div className="min-w-0">
                  <Link to={`/events/${inv.event_id}`} className="font-medium text-foreground hover:underline">{inv.event_name}</Link>
                  <p className="text-xs text-muted-foreground">
                    {fmt(inv.starts_at)}
                    {inv.sport_name ? ` - ${inv.sport_name}` : ""} - hosted by {inv.host_school_name}
                  </p>
                  <p className="text-xs text-muted-foreground">Invited: {inv.invited_school_name}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" onClick={() => respond(inv, "accepted")} disabled={busy} data-action="accept-invite">
                    {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />} Accept
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => respond(inv, "declined")} disabled={busy} data-action="decline-invite">
                    <X className="mr-1 h-4 w-4" /> Decline
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
